import { z } from 'zod';

export const posProductMergeVariantChoiceSchema = z.object({
  source_variant_id: z.string().uuid(),
  target_variant_id: z.string().uuid().nullable(),
}).strict();

/** Null variant destination explicitly means retain the original distinct identity. */
export const posProductMergeChoicesSchema = z.object({
  master_data: z.enum(['source', 'target']),
  variants: z.array(posProductMergeVariantChoiceSchema).max(500),
  suppliers: z.array(z.object({
    supplier_id: z.string().uuid(),
    product_id: z.string().uuid(),
  }).strict()).max(500),
  prices: z.array(z.object({
    price_list_id: z.string().uuid(),
    variant_id: z.string().uuid().nullable(),
    price_list_item_id: z.string().uuid(),
  }).strict()).max(1000),
}).strict();
export type PosProductMergeChoices = z.infer<typeof posProductMergeChoicesSchema>;

export const posProductMergePreviewInputSchema = z.object({
  source_product_id: z.string().uuid(),
  target_product_id: z.string().uuid(),
  reason: z.string().trim().min(5).max(2000),
  choices: posProductMergeChoicesSchema,
}).strict();
export type PosProductMergePreviewInput = z.infer<typeof posProductMergePreviewInputSchema>;

export const posProductMergeConfirmInputSchema = posProductMergePreviewInputSchema.extend({
  operation_id: z.string().uuid(),
  preview_token: z.string().regex(/^[a-f0-9]{64}$/),
  confirmation: z.literal('UNISCI'),
}).strict();
export type PosProductMergeConfirmInput = z.infer<typeof posProductMergeConfirmInputSchema>;

export const posProductMergeConflictSchema = z.object({
  code: z.string(),
  message: z.string(),
  blocking: z.boolean(),
});
export type PosProductMergeConflict = z.infer<typeof posProductMergeConflictSchema>;

export const posProductMergeLinkedKindSchema = z.enum([
  'canvas', 'composition', 'commission_addon', 'value_package',
  'skill', 'calendar_service', 'calendar_form', 'appointment',
]);
export const posProductMergeLinkedImpactSchema = z.object({
  kind: posProductMergeLinkedKindSchema,
  record_id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  description: z.string(),
  requires_resolution: z.boolean(),
});
export type PosProductMergeLinkedImpact = z.infer<typeof posProductMergeLinkedImpactSchema>;

export const posProductMergeProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  sku: z.string().nullable(),
  ean: z.string().nullable(),
  product_nature: z.string(),
  unit_of_measure_id: z.string().nullable(),
  serial_mode: z.string(),
  track_inventory: z.boolean(),
  vat_rate_id: z.string().nullable(),
  product_status: z.string(),
  alternate_eans: z.array(z.string()).optional(),
  group_alternate_eans: z.array(z.object({
    original_product_id: z.string().uuid(), ean: z.string(),
  })).optional(),
  attributes_schema_available: z.boolean().optional(),
  attributes: z.array(z.object({
    attribute_name: z.string(), attribute_values: z.unknown(), sort_order: z.number().nullable(),
  })).optional(),
}).passthrough();

export const posProductMergePreviewSchema = z.object({
  source: posProductMergeProductSchema,
  target: posProductMergeProductSchema,
  member_product_ids: z.array(z.string().uuid()),
  choices: posProductMergeChoicesSchema,
  preview_token: z.string(),
  confirmable: z.boolean(),
  conflicts: z.array(posProductMergeConflictSchema),
  variants: z.array(z.object({
    id: z.string().uuid(), product_id: z.string().uuid(),
    sku: z.string().nullable(), ean: z.string().nullable(),
    variant_attributes: z.record(z.unknown()),
  }).passthrough()),
  suppliers: z.array(z.object({
    id: z.string().uuid(), product_id: z.string().uuid(),
    supplier_id: z.string().uuid(), supplier_sku: z.string(),
  }).passthrough()),
  prices: z.array(z.object({
    id: z.string().uuid(), product_id: z.string().uuid(),
    price_list_id: z.string().uuid(), variant_id: z.string().uuid().nullable(),
    price: z.union([z.string(), z.number()]).nullable(),
  }).passthrough()),
  impacts: z.object({
    stock: z.array(z.object({
      id: z.string().uuid(), product_id: z.string().uuid(), warehouse_id: z.string().uuid(),
      variant_id: z.string().uuid().nullable(), lot_id: z.string().uuid().nullable(),
      logistic_status: z.string(), quantity: z.union([z.string(), z.number()]),
      reserved_quantity: z.union([z.string(), z.number()]).nullable(),
    }).passthrough()),
    counts: z.record(z.number()),
    linked_configurations: z.array(posProductMergeLinkedImpactSchema).optional(),
    known_stock_value: z.union([z.string(), z.number()]).nullable(),
    cost_coverage_complete: z.boolean(),
    warning: z.string(),
  }),
});
export type PosProductMergePreview = z.infer<typeof posProductMergePreviewSchema>;

export const posProductMergeResultSchema = z.object({
  merge_id: z.string().uuid(),
  operation_id: z.string().uuid(),
  source_product_id: z.string().uuid(),
  target_product_id: z.string().uuid(),
  actor_id: z.string().uuid(),
  merged_at: z.string(),
});
export type PosProductMergeResult = z.infer<typeof posProductMergeResultSchema>;

/** Provenance only: deliberately omits cost snapshots and raw audit payload. */
export const posProductMergeHistorySchema = z.object({
  original_product_id: z.string().uuid(),
  canonical_product_id: z.string().uuid(),
  canonical_product_name: z.string(),
  merges: z.array(posProductMergeResultSchema.extend({
    source_name: z.string(),
    target_name: z.string(),
    reason: z.string(),
  })),
});
export type PosProductMergeHistory = z.infer<typeof posProductMergeHistorySchema>;

export const posProductIdentitySchema = z.object({
  original_product_id: z.string().uuid(),
  canonical_product_id: z.string().uuid(),
  original_variant_id: z.string().uuid().nullable(),
  canonical_variant_id: z.string().uuid().nullable(),
  member_product_ids: z.array(z.string().uuid()),
});
export type PosProductIdentity = z.infer<typeof posProductIdentitySchema>;